import React from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';

interface PyodideLoadingIndicatorProps {
  status: 'idle' | 'loading' | 'ready' | 'error';
  progress: number;
  error?: string;
}

/**
 * Pyodide 加载进度指示器
 * 
 * 显示 Pyodide 环境的加载状态和进度
 * 用于 Python 答题页面顶部提示用户环境准备情况
 */
export default function PyodideLoadingIndicator({
  status,
  progress,
  error,
}: PyodideLoadingIndicatorProps) {
  // idle 或 ready 状态不显示
  if (status === 'idle' || status === 'ready') {
    return null;
  }

  // loading 状态：显示进度条
  if (status === 'loading') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4"
      >
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-800">
              正在加载 Python 运行环境...
            </p>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-2 bg-blue-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-blue-600 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <span className="text-xs text-blue-600 font-mono min-w-[3ch]">
                {Math.round(progress)}%
              </span>
            </div>
            <p className="text-xs text-blue-500 mt-1">
              首次加载需要下载约 30MB 资源，请耐心等待
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // error 状态：显示错误信息
  if (status === 'error') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">
              Python 环境加载失败
            </p>
            <p className="text-xs text-red-600 mt-1">
              {error || '未知错误'}
            </p>
            <p className="text-xs text-red-500 mt-2">
              含 input() 的代码将自动使用云端执行，简单代码可能无法本地预览
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
}
